/**
 * Time calculation and formatting utilities for roster/WFM
 * All times stored in UTC - convert to Australia/Sydney for display
 */

// Site timezone constant
export const SITE_TIMEZONE = 'Australia/Sydney';

/**
 * Parse ISO datetime string to Date object
 */
export function parseDateTime(isoString) {
  if (!isoString) return null;
  return new Date(isoString);
}

/**
 * Format time for display in site timezone
 * @param {string|Date} datetime - ISO string or Date object (UTC)
 * @returns {string} Formatted time like "06:30"
 */
export function formatTimeLocal(datetime) {
  if (!datetime) return '';
  const dt = typeof datetime === 'string' ? new Date(datetime) : datetime;
  return dt.toLocaleTimeString('en-AU', {
    timeZone: SITE_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Format date for display in site timezone
 * @param {string|Date} datetime - ISO string or Date object (UTC)
 * @returns {string} Formatted date like "Mon Mar 2nd"
 */
export function formatDateLocal(datetime) {
  if (!datetime) return '';
  const dt = typeof datetime === 'string' ? new Date(datetime) : datetime;
  return dt.toLocaleDateString('en-AU', {
    timeZone: SITE_TIMEZONE,
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Format full datetime for display
 * @param {string|Date} datetime - ISO string or Date object (UTC)
 * @returns {string} Formatted datetime like "Mon 2 Mar 06:30"
 */
export function formatDateTimeLocal(datetime) {
  if (!datetime) return '';
  const dt = typeof datetime === 'string' ? new Date(datetime) : datetime;
  return dt.toLocaleString('en-AU', {
    timeZone: SITE_TIMEZONE,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Calculate hours between two datetimes
 * Handles overnight shifts correctly
 * @param {string|Date} start 
 * @param {string|Date} end 
 * @param {number} breakMinutes - Optional break duration in minutes
 * @returns {number} Hours as decimal (e.g., 10.75)
 */
export function calculateHours(start, end, breakMinutes = 0) {
  if (!start || !end) return 0;
  
  const startDate = typeof start === 'string' ? new Date(start) : start;
  const endDate = typeof end === 'string' ? new Date(end) : end;
  
  let diffMs = endDate.getTime() - startDate.getTime();
  
  // Handle overnight shifts (end is next day)
  if (diffMs < 0) {
    diffMs += 24 * 60 * 60 * 1000; // Add 24 hours
  }
  
  const hours = diffMs / (1000 * 60 * 60);
  const netHours = Math.max(0, hours - (breakMinutes / 60));
  
  return Math.round(netHours * 100) / 100;
}

/**
 * Format hours as display string
 * @param {number} hours - Decimal hours
 * @returns {string} Like "10.75hrs" or "10h 45m"
 */
export function formatHours(hours) {
  if (hours === null || hours === undefined || hours === 0) return '0h';
  
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  
  if (m > 0) {
    return `${h}h ${m}m`;
  }
  return `${h}h`;
}

/**
 * Format hours as decimal display
 * @param {number} hours - Decimal hours
 * @returns {string} Like "10.75hrs"
 */
export function formatHoursDecimal(hours) {
  if (hours === null || hours === undefined || hours === 0) return '0hrs';
  return `${hours.toFixed(2)}hrs`;
}

/**
 * Create a datetime string for form input (local time)
 * @param {Date} date - Date object
 * @returns {string} YYYY-MM-DD format
 */
export function toDateInputValue(date) {
  if (!date) return '';
  const dt = typeof date === 'string' ? new Date(date) : date;
  // Get date in site timezone
  const parts = dt.toLocaleDateString('en-CA', { timeZone: SITE_TIMEZONE }).split('-');
  return parts.join('-');
}

/**
 * Create a time string for form input (local time)
 * @param {Date} date - Date object
 * @returns {string} HH:MM format
 */
export function toTimeInputValue(date) {
  if (!date) return '';
  const dt = typeof date === 'string' ? new Date(date) : date;
  return dt.toLocaleTimeString('en-AU', {
    timeZone: SITE_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * Create UTC ISO string from local date and time inputs
 * Interprets the input as Australia/Sydney time and converts to UTC
 * 
 * @param {string} dateStr - YYYY-MM-DD
 * @param {string} timeStr - HH:MM
 * @returns {string} ISO 8601 UTC string
 */
export function localInputsToUTC(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  
  // Parse inputs
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  
  // Validate parsed values
  if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute)) {
    console.error('[localInputsToUTC] Invalid input:', { dateStr, timeStr });
    return null;
  }
  
  // Method 1: Use Temporal-like approach with explicit offset calculation
  // Create a reference date in UTC with the target Sydney time values
  const refDate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
  
  // Get what Sydney time this UTC date represents
  const sydneyParts = new Intl.DateTimeFormat('en-AU', {
    timeZone: SITE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(refDate);
  
  // Extract Sydney time components
  const getSydneyPart = (type) => {
    const part = sydneyParts.find(p => p.type === type);
    return part ? parseInt(part.value, 10) : 0;
  };
  
  const sydneyHour = getSydneyPart('hour');
  const sydneyMinute = getSydneyPart('minute');
  const sydneyDay = getSydneyPart('day');
  const sydneyMonth = getSydneyPart('month');
  const sydneyYear = getSydneyPart('year');
  
  // Calculate the offset: (Sydney time from UTC reference) - (target Sydney time)
  // This tells us how much to adjust
  const targetMinutes = hour * 60 + minute;
  const sydneyMinutes = sydneyHour * 60 + sydneyMinute;
  
  // Handle day boundary cases
  let dayDiff = 0;
  if (sydneyDay !== day) {
    dayDiff = sydneyDay > day ? 1 : -1;
  }
  
  // Offset in minutes between what UTC shows in Sydney and what we want
  const offsetMinutes = (sydneyMinutes + dayDiff * 24 * 60) - targetMinutes;
  
  // Adjust the reference date by subtracting the offset
  const utcMs = refDate.getTime() - (offsetMinutes * 60 * 1000);
  const result = new Date(utcMs).toISOString();
  
  // Debug logging (can be removed in production)
  console.log('[localInputsToUTC] Input:', dateStr, timeStr, 'Sydney →', result, 'UTC');
  
  return result;
}

/**
 * Get day of week from date (in site timezone)
 * @param {string|Date} date 
 * @returns {number} 0-6 (Sunday-Saturday)
 */
export function getDayOfWeek(date) {
  if (!date) return null;
  const dt = typeof date === 'string' ? new Date(date) : date;
  const dayStr = dt.toLocaleDateString('en-AU', { timeZone: SITE_TIMEZONE, weekday: 'narrow' });
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  return days.indexOf(dayStr);
}

/**
 * Check if date is weekend (in site timezone)
 * @param {string|Date} date 
 * @returns {boolean}
 */
export function isWeekend(date) {
  const day = getDayOfWeek(date);
  return day === 0 || day === 6;
}



/**
 * Get the Monday of the current week in Sydney timezone (ISO standard)
 * @returns {Date} Monday 00:00 in Sydney time as a Date object
 */
export function getCurrentWeekMondaySydney() {
  // Get current date in Sydney timezone
  const nowStr = new Date().toLocaleString('en-US', { 
    timeZone: SITE_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
  
  // Parse the Sydney time
  const [datePart] = nowStr.split(', ');
  const [month, day, year] = datePart.split('/').map(Number);
  
  // Create date and find Monday
  const sydneyDate = new Date(year, month - 1, day);
  const dayOfWeek = sydneyDate.getDay();
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  
  sydneyDate.setDate(sydneyDate.getDate() + diffToMonday);
  sydneyDate.setHours(0, 0, 0, 0);
  
  return sydneyDate;
}

/**
 * Check if a date is in the current week (Sydney timezone, ISO standard Mon-Sun)
 * @param {Date|string} date - Date to check
 * @returns {boolean}
 */
export function isCurrentWeek(date) {
  const dt = typeof date === 'string' ? new Date(date) : date;
  const monday = getCurrentWeekMondaySydney();
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  
  return dt >= monday && dt <= sunday;
}
